import { DenormalizedBulkOrder, DenormalizedCustomizableQuestion } from '@packages/types'
import React from 'react'

import { DenormalizedBulkOrderRow } from 'customizer/bulkOrder/reducer'
import { getQuestionRestrictedAnswers } from 'customizer/customization/utils'
import * as questionPanelUtils from 'customizer/questionPanel/utils'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import BulkOrderListRowFooter from 'themes/common/components/bulkOrder/questionPanel/list/BulkOrderListRowFooter'
import useBulkOrderQuestionProps from 'themes/common/hooks/useBulkOrderQuestionProps'

import Step from '../Step'

interface BulkOrderRowFormProps {
  bulkOrderGroup: DenormalizedBulkOrder
  row: DenormalizedBulkOrderRow
  rowIndex: number
  rows: DenormalizedBulkOrderRow[]
}

const BulkOrderRowForm = ({ bulkOrderGroup, row, rowIndex, rows }: BulkOrderRowFormProps) => {
  const questionStyle = useThemeSettings('question')

  const children = row.questions?.length > 0 ? row.questions : [{ ...row }]
  const questions = questionPanelUtils.getChildrenInQuestionPanel({ children, ignoreIsHiddenWhenEmpty: true })

  const {
    createLogoAnswer,
    createAssetAnswer,
    createTextAnswer,
    removeAnswer,
    selectAnswer,
    selectAnswers,
    updateTextAnswer,
    createColorAnswer,
    updateColorAnswer,
    updateQuantityInBulkOrder,
  } = useBulkOrderQuestionProps(rowIndex)

  return (
    <>
      <div className="step" id={`step-${bulkOrderGroup.id}`}>
        {questions.map((question: DenormalizedCustomizableQuestion) => {
          const restrictedAnswers = getQuestionRestrictedAnswers(question)
          const questionWithoutRestrictedAnswers = {
            ...question,
            answers: Array.isArray(question.answers)
              ? question.answers.filter(({ id }) => !restrictedAnswers.includes(id))
              : [],
          } as DenormalizedCustomizableQuestion

          return (
            <Step
              step={questionWithoutRestrictedAnswers}
              key={question.id}
              questionStyle={{
                fontSize: questionStyle.questionFontSize,
                color: questionStyle.questionFontColor,
                fontFamily: questionStyle.questionFontFamily?.fontFamily,
              }}
              descriptionStyle={{
                color: questionStyle.descriptionFontColor,
                fontSize: questionStyle.descriptionFontSize,
                fontFamily: questionStyle.descriptionFontFamily?.fontFamily,
              }}
              headerStyle={{
                borderBottomColor: questionStyle.dividerColor,
                borderBottomWidth: questionStyle.dividerWidth,
                paddingBottom: questionStyle.dividerWidth === '0px' ? '0' : '0.5rem',
              }}
              createLogoAnswer={createLogoAnswer}
              createAssetAnswer={createAssetAnswer}
              createTextAnswer={createTextAnswer}
              removeAnswer={(answerId: string) => removeAnswer(question.id, answerId)}
              selectAnswer={(answerId: string) => selectAnswer(question.id, answerId)}
              selectAnswers={(answerIds: string[]) => selectAnswers(question.id, answerIds)}
              updateTextAnswer={updateTextAnswer}
              createColorAnswer={createColorAnswer}
              updateColorAnswer={updateColorAnswer}
            />
          )
        })}
      </div>

      <BulkOrderListRowFooter
        rowIndex={rowIndex}
        rows={rows}
        bulkOrderGroup={bulkOrderGroup}
        updateQuantity={updateQuantityInBulkOrder}
      />
    </>
  )
}

export default BulkOrderRowForm
